import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { Search, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/utils/cn';
import type { SortOrder } from '../../../types/analytics.types';

type LeaderboardTableProps<T> = {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  sortBy: string;
  sortOrder: SortOrder;
  onSort: (columnId: string) => void;
  totalCount: number;
  countLabel?: string;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  headerActions?: React.ReactNode;
};

const SortIndicator = ({
  columnId,
  activeSortBy,
  activeSortOrder,
}: {
  columnId: string;
  activeSortBy: string;
  activeSortOrder: SortOrder;
}) => {
  if (columnId !== activeSortBy) {
    return (
      <span className="ml-1 text-gray-300">
        <ChevronUp className="h-3 w-3" />
      </span>
    );
  }
  return (
    <span className="ml-1 text-[#3d6560]">
      {activeSortOrder === 'asc' ? (
        <ChevronUp className="h-3 w-3" />
      ) : (
        <ChevronDown className="h-3 w-3" />
      )}
    </span>
  );
};

export const LeaderboardTable = <T extends { [key: string]: unknown }>({
  data,
  columns,
  search,
  onSearchChange,
  searchPlaceholder = 'Search...',
  sortBy,
  sortOrder,
  onSort,
  totalCount,
  countLabel = 'items',
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  headerActions,
}: LeaderboardTableProps<T>) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#3d6560]/20 focus:border-[#3d6560]"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            {totalCount.toLocaleString('en-US')} {countLabel}
          </span>
          {headerActions}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-gray-200">
                {headerGroup.headers.map((header) => {
                  const isSortable = header.column.columnDef.enableSorting !== false;
                  const columnId = header.column.id;
                  const isActive = columnId === sortBy;

                  return (
                    <th
                      key={header.id}
                      data-sort-active={isActive ? 'true' : undefined}
                      className={cn(
                        'py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                        isSortable && 'cursor-pointer select-none hover:text-gray-700',
                      )}
                      onClick={isSortable ? () => onSort(columnId) : undefined}
                    >
                      <div className="flex items-center">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {isSortable && (
                          <SortIndicator
                            columnId={columnId}
                            activeSortBy={sortBy}
                            activeSortOrder={sortOrder}
                          />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="py-4 px-4 text-sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasNextPage && (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
